










// Vue
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { Mixins } from 'vue-mixin-decorator';
import { mapGetters, mapActions } from 'vuex';

// Third party
import dayjs, { UnitType } from 'dayjs';
import 'dayjs/locale/es';

// Constants
import Assets from '@/constants/assets.ts';
import RouteNames from '@/constants/route-names';

// Components
import Btn, { BtnColor, BtnWidth } from '@/shared/components/btn.component.vue';
import DropUp, {
  DropUpColorHover,
} from '@/shared/components/DropUp.component.vue';
import Icon, { IconEnum } from '@/shared/components/icon.component.vue';
import Pagination from '@/shared/components/Pagination/Pagination.component.vue';
import ItemThumb from '@/shared/components/item-thumb.component.vue';
import Spinner from '@/shared/components/Spinner.component.vue';
import TableColumnOrder from '@/shared/components/TableColumnOrder.component.vue';
import ButtonSwitch, {
  SwitchActiveColor,
  ButtonSwitchPredefinedStyle
} from "@/shared/components/button-switch.component.vue";
import TopBar from '@/shared/components/TopBar.component.vue';

// Components - Views
import PurchaseRow from '@/manager/purchase/views/convocation-detail/purchase-row/index.vue';
import EditBillModal from '@/manager/billing/views/edit-bill-modal/index.vue';
import GroupFilter from '@/shared/components/GroupFilter.component.vue';

// Models - Interfaces
import { CounterColor } from '@/shared/components/counter/counter.enum';
import { CounterInterface } from '@/shared/components/counter/counter.interface';
import { Convocation } from '@/manager/convocation/convocation.model';
import { SecondLevelOptionModel } from '@/shared/models/secondLevelOption.model';
import { TimetablesInterface } from '@/shared/interfaces/timetables.interface';
import { Purchase } from '@/clickbuy/purchase/models/purchase.model';

// Services
import { PurchaseService } from '@/manager/purchase/purchase.service';
import BillingService from '@/manager/billing/billing.service';


// Mixins
import WithSpinner from '@/shared/mixins/withSpinner.mixin.ts';
import WithPagination from '@/shared/mixins/withPagination.mixin.ts';
import ColumnOrderMixin from '@/shared/mixins/columnOrder.mixin.ts';
import SearchMixin from '@/shared/mixins/search.mixin.ts';

export enum ListActions {
  DownloadCsv = 'download-csv',
  SendAllPurchaseBills = 'send-all-purchase-bills',
  SendAllBills = 'send-all-bills',
  ToConvocation = 'to-convocation'
}

export interface Filters {
  confirmed: boolean;
  refunded: boolean;
  annullate: boolean;
  total: boolean;
}

interface MixinInterface
  extends WithPagination,
  SearchMixin,
  ColumnOrderMixin { }

@Component({
  components: {
    Btn,
    GroupFilter,
    PurchaseRow,
    EditBillModal,
    DropUp,
    Icon,
    ItemThumb,
    Pagination,
    Spinner,
    TableColumnOrder,
    TopBar,
    ButtonSwitch
  },
  data() {
    return {
      purchaseListCounter: null,  // Definirlo en `data`
    };
  },
  computed: {
    ...mapGetters({
      getConvocation: 'getConvocation',
      getPurchaseList: 'getPurchaseList',
      isOpenNavbar: 'isOpenNavbar',
      getBillList: 'getBillList',
      getPermissions: 'getPermissions',
    }),
  },
})
export default class PurchaseList extends Mixins<MixinInterface>(
  WithPagination,
  SearchMixin,
  ColumnOrderMixin,
) {
  @Prop() private convocationId!: string;
  @Prop() private columnsVisibility!: any;
  @Prop() private searchValue!: string;
  @Prop() private searchNifStringValue!: string;
  @Prop() private searchFiscalDataValue!: string;
  @Prop({ default: ""}) private dateFromValue!: string;
  @Prop({ default: ""}) private dateToValue!: string;
  @Prop({ default: false }) private special!: boolean;
  @Prop() private headerClass!: string;

  private assets = Assets;
  private dropUpColorHover = DropUpColorHover;
  private iconEnum = IconEnum;
  private btnColor = BtnColor;
  private btnWidth = BtnWidth;
  private getConvocation!: any;
  private participantMassiveEmailList!: any;
  private loadingGroupFilters = true;
  private getPurchaseList!: Purchase[];
  private purchaseListCounter:any;
  private isOpenNavbar!: any;
  private lastNoteTyped: string = '';
  private interval!: any;
  private cleanSearch = false;
  private purchaseListLoaded = false;
  private mailAlbaran = { id: 0, text: "Albarán" }
  private mailFactura = { id: 1, text: "Factura" }
  private mailSwitchSelected = this.mailFactura
  private getBillList!: any;
  private modifyDataPrivateModal = false;
  private editBillModalOpened = false;
  private purchaseBill: any
  protected searchNifString:any=""
  private searchFiscalData:any=""
  private dateFrom:any=undefined
  private dateTo:any=undefined
  private filters: Filters = {
    confirmed: false,
    refunded: false,
    annullate: false,
    total: true,
  };

  @Watch('searchValue')
  public watchSearchValue(newVal: any, oldVal: any) {
    this.searchString = this.searchValue;
  }

  @Watch('searchNifStringValue')
  public watchSearchNifStringValue(newVal: any, oldVal: any) {
    this.searchNifString = this.searchNifStringValue;
    this.searchCallback();
  }

  @Watch('searchFiscalDataValue')
  public watchSearchFiscalDataValue(newVal: any, oldVal: any) {
    this.searchFiscalData = this.searchFiscalDataValue;
    this.searchCallback();
  }

  @Watch('dateFromValue')
  public watchDateFromValue(newVal: any, oldVal: any) {
    this.dateFrom = this.dateFromValue;
  }

  @Watch('dateToValue')
  public watchDateToValue(newVal: any, oldVal: any) {
    this.dateTo = this.dateToValue;
    this.searchCallback();
  }

  get openModal() {
    return this.editBillModalOpened;
  }

  get convocation() {
    return this.getConvocation(this.convocationId);
  }

  get showPagination() {
    return this.purchaseListLoaded && !this.spinner.show;
  }

  get showTable() {
    const result = this.purchaseListLoaded && !this.spinner.show;
    this.$emit('show-table', result);
    return result;
  }

  get dropUpOption() {
    return [
      { value: ListActions.DownloadCsv, name: 'Exportar CSV' },
      { value: ListActions.SendAllPurchaseBills, name: 'Enviar albaranes' },
      { value: ListActions.SendAllBills, name: 'Enviar facturas' },
      { value: ListActions.ToConvocation, name: 'Ir a participantes' },
    ];
    // return [{ value: ListActions.DownloadCsv, name: 'Exportar CSV' }];
  }

  get mailFilter(): any[] {
    return [this.mailAlbaran, this.mailFactura];
  }

  private mailFilterChanged(option: any) {
    if (option.id === this.mailAlbaran.id) {
      this.mailSwitchSelected = this.mailAlbaran;
    } else if (option.id === this.mailFactura.id) {
      this.mailSwitchSelected = this.mailFactura;
    }
    this.convocation.sendMailType = this.mailSwitchSelected.id

    this.updateConvocation()
  }

  private cleanFilters() {
    location.reload()
  }

  private updateConvocation(callback: any = () => { const doNothing = 1; }) {
    this.$store.dispatch(
      'updateConvocation',
      this.convocation
      ,
    ).then((res: any) => {
      callback();
    });
  }

  private checkMailSelected() {
    if (this.convocation && this.convocation.sendMailType == 0) {
      this.mailSwitchSelected = this.mailAlbaran
    } else if (this.convocation && this.convocation.sendMailType == 1) {
      this.mailSwitchSelected = this.mailFactura
    }
  }

  public fetchList() {
    this.requestShowSpinner();
    this.$emit('showing-spinner');
    this.fetchPurchaseList((res: any) => {
      this.onFetchSuccess(res);
      this.$emit('list-loaded', res);
      this.purchaseListLoaded = true;
      this.changeSpinnerVisibility(false);
    });
  }

  public searchCallback() {
    this.fetchFirstPageList();
  }

  private mounted() {
    this.fetchPurchaseListCounters();
  }

  private created() {
    this.changeSpinnerVisibility(true);
    this.searchString = this.searchValue;
    this.searchNifString = this.searchNifStringValue;
    this.searchFiscalData = this.searchFiscalDataValue
    this.dateFrom = this.dateFromValue
    this.dateTo = this.dateToValue
    this.fetchFirstPageList();
  }

  private refetchList() {
    this.fetchPurchaseListCounters();
    this.$emit('refetch-list');
  }

  public columnOrderFetchList() {
    this.requestShowSpinner();
    this.fetchPurchaseList((res: any) => {
      this.columnOrderOnFetchSuccess();
      this.changeSpinnerVisibility(false);
    });
  }

  private handleComeBack() {
    this.cleanSearch = !this.cleanSearch;
    if (this.searchString === '') {
      this.fetchFirstPageList();
    } else {
      this.searchString = '';
    }
  }

  private fetchPurchaseList(
    callback: any = () => {
      const doNothing = 1;
    },
  ) {
    this.$store
      .dispatch('fetchPurchaseList', {
        id: this.convocationId,
        filter: this.filters,
        offset: this.offset,
        limit: this.limit,
        ordering: this.composeOrdering(),
        search: this.searchString,
        nif: this.searchNifString,
        fiscalData: this.searchFiscalData,
        dateFrom: this.dateFrom,
        dateTo: this.dateTo,
      })
      .then((res: any) => {
        this.checkMailSelected();
        this.changeSpinnerVisibility(false);
        if (callback) {
          callback(res);
        }
      })
      .catch((err) => {
        this.changeSpinnerVisibility(false);
      });
  }

  private fetchPurchaseListCounters() {
    this.$store
      .dispatch('fetchPurchaseListCounters', {
        id: this.convocationId,
      })
      .then((res: any) => {
        this.purchaseListCounter = res;
      })
      .catch((err) => {
        console.error(err)
      });
  }

  private dropUpOptionSelected(option: any) {
    switch (option.value) {
      case ListActions.DownloadCsv:
        PurchaseService.downloadCsv(this.convocationId, {
          search: this.searchString,
          nif: this.searchNifString,
          fiscalData: this.searchFiscalData,
          dateFrom: this.dateFrom,
          dateTo: this.dateTo,
          ...this.filters
        });
        break;
      case ListActions.SendAllPurchaseBills:
        PurchaseService.sendAllBills(this.convocationId, {
          search: this.searchString,
          nif: this.searchNifString,
          fiscalData: this.searchFiscalData,
          dateFrom: this.dateFrom,
          dateTo: this.dateTo,
        }).then(
          (res) => {
            const message = res.data.details;
            this.showMessage(message);
          },
          (err) => {
            this.showMessage(err.response.data);
          },
        );;
        break;
      case ListActions.SendAllBills:
        let bill_ids = this.getPurchaseList.map(purchase =>
          purchase.bill != undefined ? purchase.bill.id : null
        ).filter(id => id !== null);

        BillingService.sendAllBills({
          bill_list: bill_ids
        });
        break;
      case ListActions.ToConvocation:
        this.$router.push({
          name: RouteNames.ConvocationDetail, params: {
            id: this.convocationId,
          }
        });
        break;
    }
  }

  private closingModal() {
    this.modifyDataPrivateModal = false
    this.setEditBillModalOpened(false);
  }

  private setModifyDataPrivate(value: boolean) {
    this.modifyDataPrivateModal = value;
  }

  private setEditBillModalOpened(value: boolean) {
    this.editBillModalOpened = value;
  }

  private purchaseBillToModal(value: any) {
    this.purchaseBill = value
  }

  private showMessage(message: string) {
    this.$emit('show-message', message);
  }

  get counterFilters(): CounterInterface[] {
    const counters = [
      {
        value: (this.purchaseListCounter) ? this.purchaseListCounter.confirmed : 0,
        title: 'Compras',
        active: this.filters.confirmed,
        color: CounterColor.Primary,
        filter: 'confirmed',
        visible: true,
      }, {
        value: (this.purchaseListCounter) ? this.purchaseListCounter.refunded : 0,
        title: 'Devoluciones',
        active: this.filters.refunded,
        color: CounterColor.Primary,
        filter: 'refunded',
        visible: true,
      }, {
        value: (this.purchaseListCounter) ? this.purchaseListCounter.annullate : 0,
        title: 'Anulada',
        active: this.filters.annullate,
        color: CounterColor.GreyDark,
        filter: 'annullate',
        visible: true,
      },
      {
        value: (this.purchaseListCounter) ? this.purchaseListCounter.total : 0,
        title: 'Total',
        active: this.filters.total,
        color: CounterColor.Primary,
        filter: 'total',
        visible: true,
      },
      
    ];

    return counters;
  }

  private changeFilter(counter: CounterInterface) {
    this.filters = {
      confirmed: false,
      refunded: false,
      annullate: false,
      total: false,
    };
    switch (counter.filter) {
      case 'confirmed':
        this.filters.confirmed = true;
        break;
      case 'refunded':
        this.filters.refunded = true;
        break;
      case 'annullate':
        this.filters.annullate = true;
        break;
      case 'total':
        this.filters.total = true;
        break;
    }
    this.requestShowSpinner();
    this.fetchFirstPageList();
  }
}
